import { makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from '@fluentui/react-components';
import { CalendarToday, ChevronLeft, ChevronRight } from '@material-symbols-svg/react/rounded/w500';
import { useLocation } from 'react-router-dom';

import { headingForSection, isMonthAwareSection, sectionFromPath } from '../app/RouteMeta';
import { nextMonth, prevMonth, useCurrentMonth } from '../calendar';
import { firstNameOf } from '../common';
import { useCurrentUser } from '../current-user';
import { useSettings } from '../settings';
import { palette, tokens } from '../theme';
import { ProfileChip } from './ProfileChip';

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const Header: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const { user } = useCurrentUser();
    const firstName = user ? firstNameOf(user.displayName) : null;
    const { configured } = useSettings();
    const { current, set, goToday, label } = useCurrentMonth();

    const section = sectionFromPath(location.pathname);
    const showMonthNav = isMonthAwareSection(section) && configured;
    const heading = headingForSection(section, firstName);

    return (
        <header className={classes.root}>
            <div className={classes.leftSlot}>
                {heading && (
                    <div className={classes.headingBlock}>
                        <h1 className={classes.pageTitle}>{heading.title}</h1>
                        <p className={classes.pageSubtitle}>{heading.subtitle}</p>
                    </div>
                )}
            </div>
            <div className={classes.centerSlot}>
                {showMonthNav && (
                    <div className={classes.monthCluster}>
                        <div className={classes.monthGroup} role="group" aria-label="Month navigation">
                            <button
                                type="button"
                                className={classes.segmentArrow}
                                aria-label="Previous month"
                                onClick={() => set(prevMonth(current))}
                            >
                                <ChevronLeft className={classes.arrowIcon} aria-hidden />
                            </button>
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <button type="button" className={classes.segmentMonth} aria-label="Pick a month">
                                        <span className={classes.monthName}>{label.month}</span>
                                        <span className={classes.yearName}>{label.year}</span>
                                    </button>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {MONTH_NAMES.map((name, idx) => {
                                            const m = idx + 1;
                                            return (
                                                <MenuItem key={name} onClick={() => set({ year: current.year, month: m })}>
                                                    {name} {current.year}
                                                </MenuItem>
                                            );
                                        })}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <button
                                type="button"
                                className={classes.segmentArrow}
                                aria-label="Next month"
                                onClick={() => set(nextMonth(current))}
                            >
                                <ChevronRight className={classes.arrowIcon} aria-hidden />
                            </button>
                        </div>
                        <Tooltip content="Jump to today" relationship="label">
                            <button type="button" className={classes.todayIconButton} aria-label="Jump to today" onClick={goToday}>
                                <CalendarToday className={classes.todayIcon} aria-hidden />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
            <div className={classes.rightSlot}>{configured && <ProfileChip />}</div>
        </header>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        minHeight: '58px',
        padding: `13px ${tokens.spacingHorizontalL} 3px`,
        backgroundColor: palette.pageBg,
        flexShrink: 0,
    },
    leftSlot: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        minWidth: 0,
    },
    centerSlot: {
        justifySelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
    },
    rightSlot: {
        justifySelf: 'end',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    monthGroup: {
        display: 'inline-flex',
        alignItems: 'stretch',
        height: '38px',
        borderRadius: palette.pillRadius,
        backgroundColor: palette.panelBg,
        boxShadow: palette.softShadowSm,
        overflow: 'hidden',
        transition: 'box-shadow 160ms ease',
        '&:hover': {
            boxShadow: palette.softShadowMd,
        },
    },
    segmentArrow: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        border: 'none',
        background: 'transparent',
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        padding: 0,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
        // Focus outline is dropped here — the surrounding `monthGroup` pill
        // clips with `overflow: hidden`, so a rectangular outline on the
        // first/last segment intersects the rounded corners awkwardly. The
        // backgroundColor flip carries the keyboard-focus signal instead.
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
    },
    segmentMonth: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: 1.1,
        color: tokens.colorNeutralForeground1,
        transition: 'background-color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
        },
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: palette.accentBg,
        },
    },
    monthName: {
        color: tokens.colorNeutralForeground1,
    },
    yearName: {
        fontWeight: 300,
        color: tokens.colorNeutralForeground3,
    },
    headingBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '0 10px',
        minWidth: 0,
    },
    pageTitle: {
        margin: 0,
        fontSize: '22px',
        fontWeight: tokens.fontWeightBold,
        lineHeight: 1.1,
        letterSpacing: '-0.2px',
        color: tokens.colorNeutralForeground1,
    },
    pageSubtitle: {
        margin: 0,
        fontSize: '13px',
        fontWeight: tokens.fontWeightRegular,
        color: palette.dimText,
        lineHeight: 1.3,
        '& em': {
            fontStyle: 'normal',
            color: palette.accent,
            fontWeight: tokens.fontWeightSemibold,
        },
    },
    monthCluster: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    todayIconButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        marginLeft: '6px',
        border: 'none',
        borderRadius: '50%',
        backgroundColor: palette.panelBg,
        boxShadow: palette.softShadowSm,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        padding: 0,
        transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
            boxShadow: palette.softShadowMd,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '2px',
        },
    },
    todayIcon: {
        width: '16px',
        height: '16px',
    },
    arrowIcon: {
        width: '18px',
        height: '18px',
    },
});
